import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormControl,
} from '@angular/forms';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SettingsService } from '../settings.service';
import { finalize, Subject, take, takeUntil } from 'rxjs';
import { ManageOrganizationDrawerParams } from '../../manage-organization.interface';
import { DrawerService } from '../../../../core/services/drawer.service';
import { OrgManagementMode } from '../../manage-organization.enums';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { ApiRespones } from '../settings.interface';
import { GENERAL_OPTION_IDS, DEVELOPMENT_AREA_IDS } from './options.enums';
import { CommonService } from '../../../../shared/services/common.service';
import { AuditFieldChange } from '../../../audit-logs/audit-logs.interface';
import { NzSpinModule } from 'ng-zorro-antd/spin';

@Component({
  selector: 'app-options',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzCheckboxModule,
    NzButtonModule,
    NzSpinModule,
  ],
  templateUrl: './options.component.html',
  styleUrls: ['./options.component.scss'],
})
export class OptionsComponent implements OnDestroy {
  optionsForm!: FormGroup;
  selectedOptions = [];
  loading = false;
  options: number[] = [];
  isUpdating = false;
  private readonly destroy$ = new Subject<void>();
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.VIEW,
    organizationName: '',
  };
  errorMessage = '';

  allOptions: { id: number; value: string }[] = [];
  generalOptions: { id: number; label: string }[] = [];
  developmentAreas: { id: number; label: string }[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly settingService: SettingsService,
    private readonly drawerService: DrawerService,
    private readonly commonService: CommonService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.fetchOptionsAndInitializeForm();
    this.setupDrawerParams();
  }

  get isEditMode(): boolean {
    return this.orgParams.mode === OrgManagementMode.EDIT;
  }
  private fetchOptionsAndInitializeForm(): void {
    this.loading = true;

    this.settingService
      .getOptionsLookup()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: options => {
          this.allOptions = options;

          this.generalOptions = options
            .filter(option => GENERAL_OPTION_IDS.includes(option.id))
            .map(option => ({ id: option.id, label: option.value }));

          this.developmentAreas = options
            .filter(option => DEVELOPMENT_AREA_IDS.includes(option.id))
            .map(option => ({ id: option.id, label: option.value }));

          this.initializeForm();
          this.loadData();
          this.cdr.detectChanges();
        },
        error: error => this.handleError(error),
      });
  }

  private initializeForm(): void {
    this.optionsForm = this.fb.group({
      generalOptions: this.fb.group(
        this.createFormControls(this.generalOptions)
      ),
      developmentAreas: this.fb.group(
        this.createFormControls(this.developmentAreas)
      ),
    });
  }

  private createFormControls(items: { id: number; label: string }[]) {
    const controls: Record<number, FormControl> = {};
    for (const item of items) {
      controls[item.id] = new FormControl(false);
    }
    return controls;
  }

  loadData(): void {
    this.loading = true;

    this.settingService
      .getOptions(this.orgParams.organizationId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (data: ApiRespones<number>) => {
          this.options = data.result.optionIds;
          this.setSelectedValues();
        },
        error: error => this.handleError(error),
      });
  }

  setSelectedValues(): void {
    const generalOptionsGroup = this.optionsForm.get(
      'generalOptions'
    ) as FormGroup;
    const developmentAreasGroup = this.optionsForm.get(
      'developmentAreas'
    ) as FormGroup;

    for (const key of Object.keys(generalOptionsGroup.controls)) {
      generalOptionsGroup.get(key)?.setValue(this.options.includes(+key));
    }

    for (const key of Object.keys(developmentAreasGroup.controls)) {
      developmentAreasGroup.get(key)?.setValue(this.options.includes(+key));
    }
  }

  getSelectedIds(): number[] {
    const selectedIds: number[] = [];

    const generalOptionsGroup = this.optionsForm.get(
      'generalOptions'
    ) as FormGroup;
    for (const key of Object.keys(generalOptionsGroup.controls)) {
      const control = generalOptionsGroup.get(key);
      if (control?.value) {
        selectedIds.push(+key);
      }
    }

    const developmentAreasGroup = this.optionsForm.get(
      'developmentAreas'
    ) as FormGroup;
    for (const key of Object.keys(developmentAreasGroup.controls)) {
      const control = developmentAreasGroup.get(key);
      if (control?.value) {
        selectedIds.push(+key);
      }
    }

    return selectedIds;
  }

  private handleError(error: Error): void {
    this.loading = false;
    this.isUpdating = false;
    this.commonService.notify('error', error.message);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  onSubmit(): void {
    const payload = {
      organizationId: this.orgParams.organizationId,
      optionIds: this.getSelectedIds(),
    };
    const _modifiedFields = this.buildOptionsModifiedFields(payload);
    const apiPayload = {
      ...payload,
      _modifiedFields,
    };

    this.isUpdating = true;
    const operation$ = this.settingService.updateOptions(apiPayload);
    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.isUpdating = false;
        })
      )
      .subscribe({
        next: () => {
          this.orgParams.mode = OrgManagementMode.VIEW;

          this.commonService.notify('success', `Options updated successfully`);
          this.loadData();
        },
        error: error => this.handleError(error),
      });
  }

  private buildOptionsModifiedFields(payload: {
    organizationId: number;
    optionIds: number[];
  }): AuditFieldChange[] {
    // Get all option IDs (union of old and new)
    const allOptionIds = Array.from(
      new Set([...this.options, ...payload.optionIds])
    );
    const changes: AuditFieldChange[] = allOptionIds.map(optionId => {
      const matched = this.allOptions.find(option => option.id === optionId);
      const label = matched?.value || optionId.toString();
      const wasSelected = this.options.includes(optionId);
      const isSelected = payload.optionIds.includes(optionId);
      return {
        field: 'optionIds',
        fieldLabel: label,
        label,
        oldValue: wasSelected ? 'Yes' : 'No',
        newValue: isSelected ? 'Yes' : 'No',
        isSensitive: false,
      } as AuditFieldChange;
    });
    return changes.filter(change => change.oldValue !== change.newValue);
  }

  enableEdit() {
    this.orgParams.mode = OrgManagementMode.EDIT;
  }
  onCancel() {
    this.orgParams.mode = OrgManagementMode.VIEW;
    this.loadData();
  }
}
